import React from "react";
import { Grid } from "semantic-ui-react";
import { Icon, Message, Tooltip } from "@weezevent/nacre";
import { FormFieldSelect, FormFieldInput } from "components/form-helpers";
import { useEventPeriodsList } from "api/api-hooks/useEventPeriodsList";
import useCheckpoints from "api/api-hooks/use-checkpoints";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import classes from "./selected-checklists.module.scss";
import { useFormContext } from "react-hook-form";
import Style from "../duplicate-checklists.module.scss";

export const SelectChecklists = ({
	organizationId,
	eventId,
	checklistsIds,
	mainEventId,
	secondaryEvents
}) => {
	const { t } = useTranslation();

	const { control } = useFormContext();

	const checkChecklistDisable = React.useCallback(
		eventsInRules => {
			if (!Array.isArray(eventsInRules)) return true;
			const allowedEvents = [mainEventId, ...secondaryEvents];
			return !eventsInRules.every(eventId =>
				allowedEvents.includes(eventId)
			);
		},
		[mainEventId, secondaryEvents]
	);

	const { data: checkpoints } = useCheckpoints({
		queryOptions: {
			select: ({ data }) =>
				data.map(({ id, name, status, events_in_rules }) => ({
					value: id,
					text: name,
					status: status,
					isDisabled: checkChecklistDisable(events_in_rules)
				}))
		},
		queryParams: { archived: false, only_stats: true }
	});

	const selectedChecklists = React.useMemo(
		() =>
			checkpoints?.filter(checklist =>
				checklistsIds.includes(checklist.value)
			) || [],
		[checklistsIds, checkpoints]
	);

	const disabledChecklists = React.useMemo(() => {
		return selectedChecklists
			.filter(({ isDisabled }) => isDisabled)
			.map(({ text }) => text);
	}, [selectedChecklists]);

	const { data: periods } = useEventPeriodsList({
		organizationId,
		eventId
	});

	const periodOptions = React.useMemo(
		() =>
			periods?.map(v => ({
				value: v.id,
				text: v.name
			})) || [],
		[periods]
	);

	const PeriodPlaceholderText = t(
		"access.checkpoint.batchChecklists.duplicateChecklists.modal.formFieldSelectPlaceholder",
		{
			count: periodOptions?.length
		}
	);

	const renderDisabledChecklistsMessage = () => {
		if (disabledChecklists.length === 0) return null;

		if (disabledChecklists.length === 1) {
			return (
				<Message
					type="danger"
					title={t(
						"access.checkpoint.batchChecklists.duplicateChecklists.disabledChecklistsMessage.title"
					)}
				>
					<div className={Style.warningContent}>
						{t(
							"access.checkpoint.batchChecklists.duplicateChecklists.disabledChecklistsMessage.descriptionSingular"
						)}
					</div>
				</Message>
			);
		}

		if (disabledChecklists.length > 1) {
			return (
				<Message
					type="danger"
					title={t(
						"access.checkpoint.batchChecklists.duplicateChecklists.disabledChecklistsMessage.title"
					)}
				>
					<div className={Style.warningContent}>
						{t(
							"access.checkpoint.batchChecklists.duplicateChecklists.disabledChecklistsMessage.descriptionPlural"
						)}
					</div>
				</Message>
			);
		}
	};

	return (
		<>
			{disabledChecklists.length > 0 && renderDisabledChecklistsMessage()}
			{selectedChecklists.length > 0 && (
				<div className={classes.formRow}>
					<Grid stackable>
						{selectedChecklists.map(
							({ value, text, isDisabled }) => {
								return (
									<Grid.Row
										key={value}
										className={classes.rowItem}
										style={{ flexWrap: "nowrap" }}
									>
										<Grid.Column width={7}>
											<Tooltip
												className={
													classes.oldItemTooltip
												}
												triggerOnHover
												title={text}
											>
												<FormFieldInput
													control={control}
													className={
														classes.oldItemInput
													}
													name={`checklist_${value}`}
													value={text}
													disabled
												/>
											</Tooltip>
										</Grid.Column>
										<Icon
											name="arrow-right"
											className={classes.formIcon}
										/>
										<Grid.Column width={7}>
											{isDisabled ? (
												<FormFieldSelect
													control={control}
													name={`periodId_${value}`}
													placeholder={t(
														"access.checkpoint.batchChecklists.duplicateChecklists.modal.notAvailable"
													)}
													disabled
													className={
														classes.notAvailableText
													}
												/>
											) : (
												<FormFieldSelect
													control={control}
													name={`periodId_${value}`}
													options={periodOptions}
													placeholder={
														PeriodPlaceholderText
													}
												/>
											)}
										</Grid.Column>
									</Grid.Row>
								);
							}
						)}
					</Grid>
				</div>
			)}
		</>
	);
};

SelectChecklists.propTypes = {
	organizationId: PropTypes.number.isRequired,
	eventId: PropTypes.number.isRequired,
	disabled: PropTypes.bool,
	checklistsIds: PropTypes.array,
	mainEventId: PropTypes.number.isRequired,
	secondaryEvents: PropTypes.array
};

SelectChecklists.defaultProps = {
	disabled: false,
	checklistsIds: [],
	secondaryEvents: []
};
